import React, { useState, useEffect, useMemo } from "react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import {
  ChevronLeft,
  Building,
  MapPin,
  Folder,
  User,
  Phone,
  Mail,
  Calendar,
  FileText,
  Shield,
  StickyNote,
  RefreshCw,
  Users,
  UserCheck,
  Clock,
  MessageSquare,
  Building2,
  Download,
  Search,
} from "lucide-react";

import {
  useGetCategoriesByAcademyQuery,
  useGetVendorsByAcademyQuery,
  useGetVisitLogsByVendorQuery,
} from "../../slices/vendorApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

const VendorManagement = () => {
  const [currentView, setCurrentView] = useState("vendorList");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || ""
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const academyId = userByEmial?.user?.academyId;

  // API Queries
  const { data: categories = [], isLoading: categoriesLoading } =
    useGetCategoriesByAcademyQuery(academyId, {
      skip: !academyId,
    });

  const { data: vendors = [], isLoading: vendorsLoading } =
    useGetVendorsByAcademyQuery(academyId, {
      skip: !academyId,
    });
  // Export Vendor List to Excel
  const exportVendorListToExcel = () => {
    try {
      if (!vendors || vendors.length === 0) {
        alert("There are no vendors to export.");
        return;
      }

      // Create data array with title and vendor data
      const exportData = [];

      // Add title and header information
      exportData.push(["LIST OF REGISTERED VENDORS"]);
      exportData.push([]); // Empty row
      exportData.push(["Report Date:", new Date().toLocaleDateString()]);
      exportData.push(["Total Vendors:", vendors.length]);
      exportData.push([]); // Empty row
      exportData.push([]); // Empty row

      // Add table headers
      exportData.push([
        "Vendor Name",
        "Address",
        "Vendor ID",
        "Contact Person",
        "Phone Number",
        "Email",
        "Category",
        "Contract Start",
        "Contract End",
        "Visit Frequency",
        "BG Serial Number",
        "BG Expiry",
        "Note",
        "Status", // Added Status column
      ]);

      // Add vendor data
      vendors.forEach((vendor) => {
        // Helper function to get category title
        const getCategoryTitle = (vendor) => {
          if (vendor.vendorCategory && vendor.vendorCategory.title) {
            return vendor.vendorCategory.title;
          }
          if (vendor.category) {
            return vendor.category;
          }
          if (vendor.vendorCategoryId) {
            const category = categories.find(
              (cat) => cat.id === vendor.vendorCategoryId
            );
            return category ? category.title : "Uncategorized";
          }
          return "Uncategorized";
        };

        // Helper function to format date
        const formatDate = (dateString) => {
          if (!dateString) return "-";
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          } catch (error) {
            return dateString;
          }
        };

        // Get vendor status based on contract end date
        const getVendorStatus = (vendor) => {
          const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
          if (!contractEndDate) return "Unknown";

          const endDate = new Date(contractEndDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return endDate >= today ? "Active" : "Expired";
        };

        exportData.push([
          vendor.name || "-",
          vendor.address || "-",
          vendor.vendorIdentity || "-",
          vendor.contactPersonName || vendor.contactPerson || "-",
          vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
          vendor.contactPersonEmail || vendor.email || "-",
          getCategoryTitle(vendor),
          formatDate(vendor.contractStartDate || vendor.contractStart),
          formatDate(vendor.contractEndDate || vendor.contractEnd),
          (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
          " per year",
          vendor.bgSerialNumber || "-",
          formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
          vendor.note || "-",
          getVendorStatus(vendor), // Added status
        ]);
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        { wch: 25 },
        { wch: 40 },
        { wch: 20 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 8 },
        { wch: 20 },
        { wch: 12 },
        { wch: 30 },
        { wch: 8 },
        { wch: 10 }, // Status column width
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Registered Vendors");

      // Generate Excel file and download
      const fileName = `registered_vendors_list_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting vendor list:", error);
      alert("Failed to export vendor list. Please try again.");
    }
  };

  // Export function for visit logs - Single sheet with vendor details at top
  const exportVendorLogsToExcel = (vendor, visitLogs) => {
    try {
      if (!vendor || !visitLogs || visitLogs.length === 0) {
        alert("No visit logs available to export.");
        return;
      }

      // Helper function to format date
      const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return dateString;
        }
      };

      // Helper function to format time
      const formatTime = (timeString) => {
        if (!timeString) return "-";
        try {
          const timeWithoutSeconds = timeString
            .split(":")
            .slice(0, 2)
            .join(":");
          const [hours, minutes] = timeWithoutSeconds.split(":");
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? "PM" : "AM";
          const displayHour = hour % 12 || 12;
          return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
        } catch (error) {
          return timeString;
        }
      };

      // Create data array for single sheet
      const exportData = [];

      // Add vendor information at the top
      exportData.push(["VENDOR VISIT LOGS REPORT"]);
      exportData.push([]);
      exportData.push(["Vendor Information:"]);
      exportData.push(["Vendor Name:", vendor.name || "-"]);
      exportData.push(["Vendor ID:", vendor.vendorIdentity || "-"]);
      exportData.push(["Address:", vendor.address || "-"]);
      exportData.push([
        "Contact Person:",
        vendor.contactPersonName || vendor.contactPerson || "-",
      ]);
      exportData.push([
        "Phone Number:",
        vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
      ]);
      exportData.push([
        "Email:",
        vendor.contactPersonEmail || vendor.email || "-",
      ]);
      exportData.push([
        "Category:",
        vendor.vendorCategory?.title || vendor.category || "Uncategorized",
      ]);
      exportData.push([
        "Contract Start:",
        formatDate(vendor.contractStartDate || vendor.contractStart),
      ]);
      exportData.push([
        "Contract End:",
        formatDate(vendor.contractEndDate || vendor.contractEnd),
      ]);
      exportData.push([
        "Visit Frequency:",
        (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
        " per year",
      ]);
      exportData.push(["BG Serial Number:", vendor.bgSerialNumber || "-"]);
      exportData.push([
        "BG Expiry:",
        formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
      ]);
      exportData.push(["Note:", vendor.note || "-"]);
      exportData.push([]);
      exportData.push(["Report Summary:"]);
      exportData.push(["Total Visit Logs:", visitLogs.length]);
      exportData.push(["Export Date:", new Date().toLocaleDateString()]);
      exportData.push([]);
      exportData.push([]); // Empty row for separation

      // Add visit logs header
      exportData.push([
        "Visit Date",
        "Start Time",
        "End Time",
        "Purpose",
        "Description",
        "Supervisor Comment",
        "Logged By",
        "Logged Date",
      ]);

      // Add visit logs data
      visitLogs.forEach((log) => {
        exportData.push([
          formatDate(log.visitDate),
          formatTime(log.startTime),
          formatTime(log.endTime),
          log.purpose || "-",
          log.log || log.description || "-",
          log.comment || log.supervisorComment || "-",
          log.loggedByName || log.loggedBy || "-",
          formatDate(log.createdAt),
        ]);
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 25 },
        { wch: 40 },
        { wch: 40 },
        { wch: 20 },
        { wch: 12 },
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Vendor Visit Logs");

      // Generate Excel file and download
      const fileName = `${vendor.name.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}_visit_logs_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting vendor logs:", error);
      alert("Failed to export visit logs. Please try again.");
    }
  };

  const getCategoryColor = (categoryTitle) => {
    console.log("Getting color for category:", categoryTitle);
    if (!categoryTitle) return "bg-gray-100 text-gray-700";

    const category = categories.find(
      (cat) =>
        cat.title === categoryTitle ||
        cat.name === categoryTitle ||
        cat.categoryName === categoryTitle
    );

    console.log("Found category:", category);
    return category?.color || "bg-gray-100 text-gray-700";
  };

  const generateColorFromTitle = (title) => {
    if (!title) return "bg-gray-100 text-gray-700";

    const colorMap = {
      maintenance: "bg-blue-100 text-blue-700",
      construction: "bg-green-100 text-green-700",
      "it services": "bg-purple-100 text-purple-700",
      cleaning: "bg-orange-100 text-orange-700",
      security: "bg-red-100 text-red-700",
      electrical: "bg-yellow-100 text-yellow-700",
      plumbing: "bg-teal-100 text-teal-700",
    };

    const normalizedTitle = title.toLowerCase().trim();
    return colorMap[normalizedTitle] || "bg-gray-100 text-gray-700";
  };

  // Navigation Functions
  const handleBackToVendorList = () => {
    setCurrentView("vendorList");
    setSelectedVendor(null);
  };

  const handleViewVendorDetails = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentView("vendorDetails");
  };

  const handleViewLogDetails = (log) => {
    setSelectedLog(log);
    setCurrentView("logPreview");
  };

  const renderCurrentView = () => {
    if (!academyId) {
      return (
        <div className="bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">
              Unable to determine academy. Please check your account.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Academy ID: {academyId || "Not found"}
            </p>
          </div>
        </div>
      );
    }

    if (categoriesLoading || vendorsLoading) {
      return (
        <div className="bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {categoriesLoading
                ? "Loading categories..."
                : "Loading vendors..."}
            </p>
          </div>
        </div>
      );
    }

    if (vendors.length > 0 && categories.length === 0) {
      console.warn("Vendors exist but no categories found");
    }

    switch (currentView) {
      case "vendorList":
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            getCategoryColor={getCategoryColor}
            categories={categories}
            onExportVendorList={exportVendorListToExcel}
          />
        );
      case "vendorDetails":
        return (
          <VendorDetails
            vendor={selectedVendor}
            onBack={handleBackToVendorList}
            onViewLog={handleViewLogDetails}
            categories={categories}
            onExportVendorLogs={exportVendorLogsToExcel}
          />
        );
      case "logPreview":
        return (
          <LogPreviewPage
            log={selectedLog}
            vendor={selectedVendor}
            onBack={() => setCurrentView("vendorDetails")}
          />
        );
      default:
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            getCategoryColor={getCategoryColor}
            categories={categories}
          />
        );
    }
  };

  return <div className="h-full bg-white p-4">{renderCurrentView()}</div>;
};

const VendorList = ({
  vendors,
  onViewVendor,
  getCategoryColor,
  categories,
  onExportVendorList,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Calculate active vendors based on contract end date
  const calculateActiveVendors = () => {
    return vendors.filter((vendor) => {
      const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
      if (!contractEndDate) return false;

      const endDate = new Date(contractEndDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return endDate >= today;
    }).length;
  };

  // Calculate statistics
  const totalVendors = vendors.length;
  const activeVendors = calculateActiveVendors();

  // Get vendor status based on contract end date
  const getVendorStatus = (vendor) => {
    const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
    if (!contractEndDate) return "Unknown";

    const endDate = new Date(contractEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate >= today ? "Active" : "Expired";
  };

  // Get status color - Vercel Style
  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-50 text-green-700 border border-green-200";
      case "Expired":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  // VisitCount component
  const VisitCount = ({ vendorId }) => {
    const { data: visitLogs, isLoading } = useGetVisitLogsByVendorQuery(
      vendorId,
      {
        skip: !vendorId,
      }
    );

    if (isLoading) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-md bg-gray-50 text-gray-600 text-xs font-medium border border-gray-200">
          Loading...
        </span>
      );
    }

    const visitCount = visitLogs?.length || 0;
    return (
      <span
        className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium"
      >
        {visitCount} {visitCount === 1 ? "" : ""}
      </span>
    );
  };


  // Get vendor category title
  const getVendorCategoryTitle = (vendor) => {
    if (!vendor) return "Uncategorized";
    if (vendor.vendorCategory && vendor.vendorCategory.title) {
      return vendor.vendorCategory.title;
    }
    if (vendor.category) {
      return vendor.category;
    }
    if (vendor.vendorCategoryId) {
      const category = categories.find(
        (cat) => cat.id === vendor.vendorCategoryId
      );
      return category ? category.title : "Uncategorized";
    }
    return "Uncategorized";
  };

  // Prepare category options for CustomSelect
  const categoryOptions = useMemo(() => {
    return categories.map((category) => ({
      value: category.title,
      label: category.title,
      id: category.id,
    }));
  }, [categories]);

  // Get category display name
  const getCategoryDisplayName = (categoryValue) => {
    if (categoryValue === "all" || !categoryValue) return "Category";
    return categoryValue;
  };

  // Filter vendors
  const filteredVendors = vendors
    .filter((vendor) => {
      const matchesSearch = vendor.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const vendorCategory = getVendorCategoryTitle(vendor).toLowerCase().trim();
      const selectedCategoryLower = selectedCategory.toLowerCase().trim();
      const matchesCategory =
        selectedCategory === "all" ||
        selectedCategory === "" ||
        vendorCategory === selectedCategoryLower;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Sort by createdAt date in descending order (newest first)
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA; // Latest first
    });

  return (
    <div className="flex flex-col h-full w-full">
      {/* Statistics Cards - Vercel Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-4xl mx-auto w-full">
        <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Users size={20} className="text-gray-700" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {totalVendors}
              </p>
              <h3 className="text-sm text-gray-500 font-medium">
                Total Vendors
              </h3>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Clock size={20} className="text-gray-700" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {activeVendors}
              </p>
              <h3 className="text-sm text-gray-500 font-medium">
                Active Vendors
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Table - Vercel Style */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col">
        {/* Header with Search and Filters - Sticky */}
        <div className="p-6 border-b border-gray-200 bg-white sticky top-0 z-20">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Vendors</h3>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm transition-all"
              />
            </div>
            {/* Category Select */}
            <CustomSelect
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Category"
              allOptionLabel="All Categories"
              getDisplayValue={getCategoryDisplayName}
              allValue="all"
              minWidth={180}
            />
            {/* Export Button */}
            <button
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onExportVendorList}
              disabled={vendors.length === 0}
            >
              <Download size={16} />
              {vendors.length === 0 ? "No Data" : "Export"}
            </button>
          </div>
        </div>

        {/* Mobile View - Cards */}
        <div className="lg:hidden overflow-y-auto flex-1">
          <div className="p-4 space-y-3">
            {filteredVendors.map((vendor) => {
              const vendorCategoryTitle = getVendorCategoryTitle(vendor);
              const status = getVendorStatus(vendor);

              return (
                <div
                  key={vendor.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
                  onClick={() => onViewVendor(vendor)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 mr-2">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base text-gray-900 truncate">
                          {vendor.name}
                        </h3>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {vendor.vendorIdentity || "No ID"}
                      </p>
                    </div>
                    <VisitCount vendorId={vendor.id} />
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="text-gray-600 line-clamp-1">
                        {vendor.address}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Phone
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="text-gray-600">
                        {vendor.contactPersonPhoneNumber || vendor.phoneNumber}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Folder
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${getCategoryColor(
                          vendorCategoryTitle
                        )}`}
                      >
                        {vendorCategoryTitle}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredVendors.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || selectedCategory !== "all"
                  ? "No vendors found matching your criteria."
                  : "No vendors found."}
              </div>
            )}
          </div>
        </div>

        {/* Desktop View - Table with Sticky Header */}
        <div className="hidden lg:block overflow-y-auto flex-1">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Vendor ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Visits
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVendors.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {searchTerm || selectedCategory !== "all"
                      ? "No vendors found matching your criteria."
                      : "No vendors found."}
                  </td>
                </tr>
              ) : (
                filteredVendors.map((vendor) => {
                  const vendorCategoryTitle = getVendorCategoryTitle(vendor);
                  const status = getVendorStatus(vendor);

                  return (
                    <tr
                      key={vendor.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onViewVendor(vendor)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {vendor.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {vendor.vendorIdentity || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {vendor.address}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {vendor.contactPersonPhoneNumber ||
                            vendor.phoneNumber ||
                            "-"}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {vendor.contactPersonEmail || vendor.email || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${getCategoryColor(
                            vendorCategoryTitle
                          )}`}
                        >
                          {vendorCategoryTitle}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${getStatusColor(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <VisitCount vendorId={vendor.id} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ... (LogPreviewPage and VendorDetails components remain the same as in your original code)
const LogPreviewPage = ({ log, vendor, onBack }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div>
      <div className="max-w-4xl mx-auto">
        {/* Log Preview Content */}
        <div className=" rounded-2xl shadow-lg p-6 border">
          <div className="space-y-6">
            {/* Header with user info and action buttons - shifted up */}
            <div className="flex justify-between items-center bg-gray-100 border-b-2 border-gray-200 pb-4 -mt-6 -mx-6 px-6 pt-4 rounded-t-2xl">
              <div className="flex items-center justify-between mb-6">
                {/* Back Button */}
                <button
                  aria-label="Back"
                  onClick={onBack}
                  className="flex w-8 h-8 sm:w-10 sm:h-10 items-center justify-center rounded-full hover:bg-gray-200 transition-colors text-gray-700"
                >
                  <ChevronLeft size={24} className="sm:w-6 sm:h-6" />
                </button>

                {/* "Logged by" Text */}
                <div className="text-center flex-1">
                  <span className="font-medium text-sm sm:text-base">
                    <span className="text-gray-600">Logged by</span>
                    <span className="text-blue-500">
                      {" "}
                      {log.loggedBy || log.loggedByName || "User"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-1 last:border-b-0">
              <div className="flex justify-between items-start mb-2 gap-4">
                <h2 className="text-3xl font-bold flex-1 break-words min-w-0">
                  {log.purpose}
                </h2>
                <p className="text-gray-600 whitespace-nowrap flex-shrink-0 text-lg">
                  {formatTime(log.startTime)} – {formatTime(log.endTime)}
                </p>
              </div>

              <p className="text-gray-600 mb-6 text-lg">
                {formatDate(log.visitDate)}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <p className="text-[#555555] whitespace-pre-wrap text-lg leading-relaxed">
                  {log.log || "No description available"}
                </p>
              </div>

              {log.comment && (
                <div>
                  <h3 className="font-semibold text-[#ABABAB] mb-3 text-sm">
                    Supervisor Comment:
                  </h3>
                  <p className="text-[#555555] whitespace-pre-wrap text-lg leading-relaxed">
                    {log.comment}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VendorDetails = ({
  vendor,
  onBack,
  onViewLog,
  categories,
  onExportVendorLogs,
}) => {
  const [sortOrder, setSortOrder] = useState("latest"); // 'latest' or 'earliest'

  // Fetch visit logs for this vendor
  const {
    data: visitLogsData,
    isLoading: logsLoading,
    error: logsError,
  } = useGetVisitLogsByVendorQuery(vendor?.id, {
    skip: !vendor?.id,
    refetchOnMountOrArgChange: true,
  });

  // Extract logs from response
  const visitLogs = visitLogsData?.visitLogs || visitLogsData || [];
  // Handle export visit logs
  const handleExportVisitLogs = () => {
    if (!visitLogs || visitLogs.length === 0) {
      alert("No visit logs available to export.");
      return;
    }
    onExportVendorLogs(vendor, visitLogs);
  };

  // Sort the logs based on selected order
  const sortedLogs = [...visitLogs].sort((a, b) => {
    const dateA = new Date(a.visitDate + "T" + a.startTime);
    const dateB = new Date(b.visitDate + "T" + b.startTime);

    if (sortOrder === "latest") {
      return dateB - dateA; // Latest first (descending)
    } else {
      return dateA - dateB; // Earliest first (ascending)
    }
  });

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  // Debug to see what data we have
  useEffect(() => {
    console.log("VendorDetails - Full Vendor Object:", vendor);
    console.log("VendorDetails - Available fields:", Object.keys(vendor || {}));
  }, [vendor]);

  // Enhanced data getter that handles both field name formats
  const getVendorField = (fieldName) => {
    // Handle different field name formats
    const fieldMap = {
      // Contact Person fields
      contactPerson: vendor?.contactPersonName || vendor?.contactPerson,
      // Vendor Identity fields
      vendorIdentity: vendor?.vendorIdentity,
      // Phone Number fields
      phoneNumber: vendor?.contactPersonPhoneNumber || vendor?.phoneNumber,
      // Email fields
      email: vendor?.contactPersonEmail || vendor?.email,
      // Contract fields
      contractStart: vendor?.contractStartDate || vendor?.contractStart,
      contractEnd: vendor?.contractEndDate || vendor?.contractEnd,
      // BG fields
      bgSerialNumber: vendor?.bgSerialNumber,
      bgExpiry: vendor?.bgExpiryDate || vendor?.bgExpiry,
      // Visit Frequency fields
      visitFrequency: vendor?.visitFrequencyPerYear || vendor?.visitFrequency,
      // Other fields (use direct access as fallback)
      name: vendor?.name,
      address: vendor?.address,
      note: vendor?.note,
    };

    return fieldMap[fieldName] || "-";
  };

  // Get vendor category title
  const getVendorCategoryTitle = () => {
    if (vendor?.vendorCategory && vendor.vendorCategory.title) {
      return vendor.vendorCategory.title;
    }
    if (vendor?.category) {
      return vendor.category;
    }
    if (vendor?.vendorCategoryId) {
      const category = categories.find(
        (cat) => cat.id === vendor.vendorCategoryId
      );
      return category ? category.title : "Uncategorized";
    }
    return "Uncategorized";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const timeWithoutSeconds = timeString.split(":").slice(0, 2).join(":");
      const [hours, minutes] = timeWithoutSeconds.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  if (!vendor) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow">
        <div className="text-center py-8">
          <p>Vendor not found</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-black text-white rounded-lg"
          >
            Back to Vendor List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          aria-label="Back"
          onClick={onBack}
          className=" md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all rounded-full"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700 sm:w-6 sm:h-6"
          />
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
              {getVendorField("name")}
            </h1>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left Side - Profile */}
        <div className="space-y-4">
          {/* Company Details Group */}
          <div className="bg-white-50 border p-6 rounded-lg">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl font-bold">Profile</h2>
                <p className="text-gray-600">Company profile</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {[
                {
                  label: "Name",
                  value: getVendorField("name"),
                  icon: Building,
                },
                {
                  label: "Vendor Identity (TPN)",
                  value: getVendorField("vendorIdentity"),
                  icon: FileText,
                },
                {
                  label: "Address",
                  value: getVendorField("address"),
                  icon: MapPin,
                },
                {
                  label: "Category",
                  value: getVendorCategoryTitle(),
                  icon: Folder,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon className="w-5 h-5 text-white bg-[#8E8E93] p-1 rounded flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Details Group */}
            <div className="space-y-4 mb-8">
              {[
                {
                  label: "Contact Person",
                  value: getVendorField("contactPerson"),
                  icon: User,
                },
                {
                  label: "Phone Number",
                  value: getVendorField("phoneNumber"),
                  icon: Phone,
                },
                { label: "Email", value: getVendorField("email"), icon: Mail },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon className="w-5 h-5 text-white bg-[#3776FB] p-1 rounded flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contract & Bank Details Group */}
            <div className="space-y-4 mb-8">
              {[
                {
                  label: "Contract Start",
                  value: formatDate(getVendorField("contractStart")),
                  icon: Calendar,
                },
                {
                  label: "Contract End",
                  value: formatDate(getVendorField("contractEnd")),
                  icon: Calendar,
                },
                {
                  label: "BG Serial Number",
                  value: getVendorField("bgSerialNumber"),
                  icon: FileText,
                },
                {
                  label: "BG Expiry",
                  value: formatDate(getVendorField("bgExpiry")),
                  icon: Shield,
                },
                {
                  label: "Visit Frequency",
                  value: getVendorField("visitFrequency")
                    ? `${getVendorField("visitFrequency")} per year`
                    : "-",
                  icon: RefreshCw,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon
                        className={`w-5 h-5 text-white p-1 rounded flex-shrink-0 ${field.label === "BG Serial Number"
                          ? "bg-black"
                          : field.label === "Visit Frequency"
                            ? "bg-[#F9971D]"
                            : "bg-[#F94035]"
                          }`}
                      />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Note */}
            {getVendorField("note") && getVendorField("note") !== "-" && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold">Note</h3>
                </div>
                <p className="text-gray-700">{getVendorField("note")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Visit Logs */}
        <div>
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold">Visit Logs</h2>
                <p className="text-gray-600">Visit logs of the vendor</p>
              </div>
              <button
                className={`px-4 py-2 border border-[#E4E4E4] rounded-lg shadow-sm flex items-center justify-center gap-1 whitespace-nowrap ${logsLoading || visitLogs.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-black hover:bg-gray-50"
                  }`}
                onClick={handleExportVisitLogs}
                disabled={logsLoading || visitLogs.length === 0}
              >
                <Download className="w-4 h-4" />
                {logsLoading
                  ? "Loading..."
                  : visitLogs.length === 0
                    ? "No Logs to Export"
                    : "Export Visit Logs"}
              </button>
            </div>

            <div className="mb-4">
              <select
                value={sortOrder}
                onChange={handleSortChange}
                className="w-half p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent"
              >
                <option value="latest">Latest</option>
                <option value="earliest">Earliest</option>
              </select>
            </div>

            <div className="space-y-4 max-h-[655px] overflow-y-auto pr-2">
              {logsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading visit logs...</p>
                </div>
              ) : logsError ? (
                <div className="text-center py-8 text-red-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium">Error loading visit logs</p>
                  <p className="text-sm mt-1">
                    {logsError?.data?.message ||
                      logsError?.error ||
                      "Unable to load visit logs. Please try again."}
                  </p>
                </div>
              ) : sortedLogs && sortedLogs.length > 0 ? (
                sortedLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-white p-4 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onViewLog(log)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm text-gray-500">
                        {formatDate(log.visitDate)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatTime(log.startTime)} - {formatTime(log.endTime)}
                      </span>
                    </div>
                    <h3 className="font-semibold mb-2">{log.purpose}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {log.log || "No description"}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No visit logs found for this vendor.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorManagement;
